interface SemanticVersion {
  major: number;
  minor: number;
  patch: number;
}

/**
 * Parse a semantic version string into its components
 * @param version - Version string in format "major.minor.patch"
 * @returns Parsed version object or null if invalid
 */
export const parseVersion = (version: string): SemanticVersion | null => {
  if (!version || typeof version !== "string") {
    return null;
  }

  const parts = version.split(".");

  if (parts.length !== 3) {
    return null;
  }

  const major = parseInt(parts[0], 10);
  const minor = parseInt(parts[1], 10);
  const patch = parseInt(parts[2], 10);

  if (isNaN(major) || isNaN(minor) || isNaN(patch)) {
    return null;
  }

  return { major, minor, patch };
};

/**
 * Determine if a client should force reload based on version comparison
 * Force reload occurs when major versions differ
 * @param clientVersion - Current client version string
 * @param serverVersion - Server version string
 * @returns true if force reload is required, false otherwise
 */
export const shouldForceReload = (
  clientVersion: string,
  serverVersion: string,
): boolean => {
  const client = parseVersion(clientVersion);
  const server = parseVersion(serverVersion);

  // If either version is invalid, don't force reload
  if (!client || !server) {
    console.warn(
      "Invalid version format detected:",
      { clientVersion, serverVersion },
    );
    return false;
  }

  // Force reload only when major versions differ
  return client.major !== server.major;
};

/**
 * Compare two versions to determine if server is newer
 * @param clientVersion - Current client version string
 * @param serverVersion - Server version string
 * @returns true if server version is newer, false otherwise
 */
export const isServerVersionNewer = (
  clientVersion: string,
  serverVersion: string,
): boolean => {
  const client = parseVersion(clientVersion);
  const server = parseVersion(serverVersion);

  if (!client || !server) {
    return false;
  }

  // Compare major version
  if (server.major > client.major) return true;
  if (server.major < client.major) return false;

  // Compare minor version
  if (server.minor > client.minor) return true;
  if (server.minor < client.minor) return false;

  // Compare patch version
  return server.patch > client.patch;
};

